import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Users, Clock, CheckCircle, XCircle, Crown,
  Download, Filter, Search, RefreshCw, Mail, Phone,
  Building, Calendar, AlertTriangle, TrendingUp, DollarSign,
  Shield, Truck, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Drill-down configurations for admin metrics
const ADMIN_DRILL_DOWN_CONFIG = {
  'total-applications': {
    title: 'All Applications',
    subtitle: 'Complete application overview',
    icon: Users,
    color: '#3B82F6',
  },
  'pending-applications': {
    title: 'Pending Applications',
    subtitle: 'Applications awaiting review',
    icon: Clock,
    color: '#F59E0B',
  },
  'approved-carriers': {
    title: 'Approved Carriers',
    subtitle: 'Successfully onboarded carriers',
    icon: CheckCircle,
    color: '#10B981',
  },
  'rejected-applications': {
    title: 'Rejected Applications',
    subtitle: 'Declined applications',
    icon: XCircle,
    color: '#EF4444',
  },
  'total-users': {
    title: 'All Hive Users',
    subtitle: 'Complete user directory',
    icon: Crown,
    color: '#D4AF37',
  },
  'trial-users': {
    title: 'Trial Users',
    subtitle: 'Users in trial period',
    icon: Clock,
    color: '#F59E0B',
  },
  'active-users': {
    title: 'Active Users',
    subtitle: 'Users with active subscriptions',
    icon: CheckCircle,
    color: '#10B981',
  },
  'suspended-users': {
    title: 'Suspended Users',
    subtitle: 'Users with suspended accounts',
    icon: AlertTriangle,
    color: '#EF4444',
  },
  'pending-invites': {
    title: 'Pending Invites',
    subtitle: 'Invitations awaiting setup',
    icon: Mail,
    color: '#3B82F6',
  },
};

// Mock data generator
const getMockData = (metricType) => {
  const mockApplications = [
    { id: 'APP-001', company: 'Swift Transport LLC', contact: 'John Smith', email: 'john@swift.com', phone: '555-0101', dot: '123456', mc: 'MC-789012', status: 'pending', submitted: '02/24/2026', tier: 'elite' },
    { id: 'APP-002', company: 'Highway Express Inc', contact: 'Jane Doe', email: 'jane@highway.com', phone: '555-0102', dot: '234567', mc: 'MC-890123', status: 'approved', submitted: '02/23/2026', tier: 'professional' },
    { id: 'APP-003', company: 'Premier Freight Co', contact: 'Bob Wilson', email: 'bob@premier.com', phone: '555-0103', dot: '345678', mc: 'MC-901234', status: 'pending', submitted: '02/22/2026', tier: 'basic' },
    { id: 'APP-004', company: 'Atlas Trucking', contact: 'Sarah Lee', email: 'sarah@atlas.com', phone: '555-0104', dot: '456789', mc: 'MC-012345', status: 'rejected', submitted: '02/21/2026', tier: 'elite' },
    { id: 'APP-005', company: 'Golden Mile Logistics', contact: 'Mike Brown', email: 'mike@golden.com', phone: '555-0105', dot: '567890', mc: 'MC-123456', status: 'approved', submitted: '02/20/2026', tier: 'professional' },
  ];

  const mockUsers = [
    { id: 'USR-001', name: 'RNB Trucking LLC', email: 'carrier.build@test.com', status: 'starter_pack', tier: 'elite', joined: '02/25/2026', lastActive: '02/25/2026' },
    { id: 'USR-002', name: 'OTFRNB Logistics LLC', email: 'carrier.bronze@test.com', status: 'active', tier: 'bronze', joined: '02/01/2026', lastActive: '02/24/2026' },
    { id: 'USR-003', name: 'Noah Freight Co', email: 'carrier.silver@test.com', status: 'active', tier: 'silver', joined: '01/15/2026', lastActive: '02/25/2026' },
    { id: 'USR-004', name: 'Queen B Trucking LLC', email: 'carrier.gold@test.com', status: 'active', tier: 'gold', joined: '12/01/2025', lastActive: '02/25/2026' },
    { id: 'USR-005', name: 'Test Carrier Inc', email: 'test@carrier.com', status: 'trial', tier: 'bronze', joined: '02/20/2026', lastActive: '02/22/2026' },
  ];

  switch (metricType) {
    case 'total-applications':
      return { items: mockApplications, type: 'applications' };
    case 'pending-applications':
      return { items: mockApplications.filter(a => a.status === 'pending'), type: 'applications' };
    case 'approved-carriers':
      return { items: mockApplications.filter(a => a.status === 'approved'), type: 'applications' };
    case 'rejected-applications':
      return { items: mockApplications.filter(a => a.status === 'rejected'), type: 'applications' };
    case 'total-users':
      return { items: mockUsers, type: 'users' };
    case 'trial-users':
      return { items: mockUsers.filter(u => u.status === 'trial'), type: 'users' };
    case 'active-users':
      return { items: mockUsers.filter(u => u.status === 'active'), type: 'users' };
    case 'suspended-users':
      return { items: mockUsers.filter(u => u.status === 'suspended'), type: 'users' };
    case 'pending-invites':
      return { items: mockUsers.filter(u => u.status === 'pending_invite'), type: 'users' };
    default:
      return { items: [], type: 'unknown' };
  }
};

// Applications Table Component
const ApplicationsTable = ({ data }) => (
  <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-zinc-800/50">
          <tr className="text-left text-zinc-400 text-xs uppercase">
            <th className="p-4">ID</th>
            <th className="p-4">Company</th>
            <th className="p-4">Contact</th>
            <th className="p-4">DOT/MC</th>
            <th className="p-4">Tier</th>
            <th className="p-4">Status</th>
            <th className="p-4">Submitted</th>
            <th className="p-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800">
          {data.map((app) => (
            <tr key={app.id} className="hover:bg-zinc-800/50">
              <td className="p-4 text-[#D4AF37] font-mono text-sm">{app.id}</td>
              <td className="p-4">
                <div>
                  <p className="text-white font-medium">{app.company}</p>
                  <p className="text-zinc-500 text-sm">{app.email}</p>
                </div>
              </td>
              <td className="p-4">
                <div>
                  <p className="text-white">{app.contact}</p>
                  <p className="text-zinc-500 text-sm">{app.phone}</p>
                </div>
              </td>
              <td className="p-4 text-zinc-400 text-sm">
                <p>DOT: {app.dot}</p>
                <p>{app.mc}</p>
              </td>
              <td className="p-4">
                <span className={`px-2 py-1 text-xs font-medium uppercase ${
                  app.tier === 'elite' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                  app.tier === 'professional' ? 'bg-purple-500/20 text-purple-400' :
                  'bg-zinc-700 text-zinc-300'
                }`}>
                  {app.tier}
                </span>
              </td>
              <td className="p-4">
                <span className={`px-2 py-1 text-xs font-medium ${
                  app.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {app.status}
                </span>
              </td>
              <td className="p-4 text-zinc-400 text-sm">{app.submitted}</td>
              <td className="p-4">
                <button className="px-3 py-1 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F5D47B]">
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Users Table Component
const UsersTable = ({ data }) => (
  <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-zinc-800/50">
          <tr className="text-left text-zinc-400 text-xs uppercase">
            <th className="p-4">ID</th>
            <th className="p-4">Company</th>
            <th className="p-4">Email</th>
            <th className="p-4">Tier</th>
            <th className="p-4">Status</th>
            <th className="p-4">Joined</th>
            <th className="p-4">Last Active</th>
            <th className="p-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800">
          {data.map((user) => (
            <tr key={user.id} className="hover:bg-zinc-800/50">
              <td className="p-4 text-[#D4AF37] font-mono text-sm">{user.id}</td>
              <td className="p-4 text-white font-medium">{user.name}</td>
              <td className="p-4 text-zinc-400">{user.email}</td>
              <td className="p-4">
                <span className={`px-2 py-1 text-xs font-medium uppercase ${
                  user.tier === 'gold' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                  user.tier === 'silver' ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' :
                  user.tier === 'elite' ? 'bg-purple-500/20 text-purple-400' :
                  'bg-[#CD7F32]/20 text-[#CD7F32]'
                }`}>
                  {user.tier}
                </span>
              </td>
              <td className="p-4">
                <span className={`px-2 py-1 text-xs font-medium ${
                  user.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  user.status === 'trial' ? 'bg-yellow-500/20 text-yellow-400' :
                  user.status === 'starter_pack' ? 'bg-blue-500/20 text-blue-400' :
                  user.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                  'bg-zinc-700 text-zinc-300'
                }`}>
                  {user.status.replace('_', ' ')}
                </span>
              </td>
              <td className="p-4 text-zinc-400 text-sm">{user.joined}</td>
              <td className="p-4 text-zinc-400 text-sm">{user.lastActive}</td>
              <td className="p-4">
                <div className="flex gap-2">
                  <button className="px-3 py-1 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F5D47B]">
                    View
                  </button>
                  <button className="px-3 py-1 bg-zinc-700 text-white text-sm hover:bg-zinc-600">
                    Impersonate
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Main Admin Drill-Down Page
const AdminDrillDownPage = () => {
  const navigate = useNavigate();
  const { metricType } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const config = ADMIN_DRILL_DOWN_CONFIG[metricType];
  
  useEffect(() => {
    // Verify admin auth
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    // Load mock data
    setIsLoading(true);
    setTimeout(() => {
      setData(getMockData(metricType));
      setIsLoading(false);
    }, 300);
  }, [metricType, navigate]);
  
  if (!config) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Metric Not Found</h2>
          <p className="text-zinc-500 mb-4">The requested drill-down view doesn't exist.</p>
          <button 
            onClick={() => navigate('/admin')}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  const Icon = config.icon;
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-drill-down-page">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Admin</span>
            </button>
            <div className="h-6 w-px bg-zinc-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
                <Icon className="w-5 h-5" style={{ color: config.color }} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {config.title}
                </h1>
                <p className="text-zinc-500 text-sm">{config.subtitle}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm w-64"
              />
            </div>
            <button className="px-4 py-2 bg-zinc-800 text-zinc-400 hover:text-white text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button className="px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </header>
      
      {/* Summary Stats */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-zinc-500 text-xs uppercase">Total Records</p>
            <p className="text-2xl font-bold text-white">{data?.items?.length || 0}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-zinc-500 text-xs uppercase">This Week</p>
            <p className="text-2xl font-bold text-[#D4AF37]">{Math.floor((data?.items?.length || 0) * 0.3)}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-zinc-500 text-xs uppercase">This Month</p>
            <p className="text-2xl font-bold text-white">{Math.floor((data?.items?.length || 0) * 0.7)}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-zinc-500 text-xs uppercase">Growth</p>
            <p className="text-2xl font-bold text-green-400">+12%</p>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 pb-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : data?.type === 'applications' ? (
          <ApplicationsTable data={data.items} />
        ) : data?.type === 'users' ? (
          <UsersTable data={data.items} />
        ) : (
          <div className="text-center py-12">
            <p className="text-zinc-500">No data available</p>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-zinc-800 px-6 py-4">
        <p className="text-zinc-600 text-xs text-center max-w-7xl mx-auto">
          Queen B Logistics Admin • Data as of {new Date().toLocaleDateString()}
        </p>
      </footer>
    </div>
  );
};

export default AdminDrillDownPage;
